/**
 * Roles and the people-management permission matrix (owner / admin / member / guest). The API enforces exactly these
 * rules (apps/api WorkspaceDO); clients use them to show only what the server will allow. Pure.
 *
 * | actor ↓ / may…                          | owner | admin                 | member, guest, agent |
 * |-----------------------------------------|-------|-----------------------|----------------------|
 * | promote to owner / change an owner      | yes¹  | no                    | no                   |
 * | change an admin's role                  | yes   | only their own (step down) | no             |
 * | change members and guests (→ admin too) | yes   | yes                   | no                   |
 * | deactivate / reactivate owners          | yes¹  | no                    | no                   |
 * | deactivate / reactivate admins          | yes   | no                    | no                   |
 * | deactivate / reactivate members, guests | yes   | yes                   | no                   |
 * | transfer primary ownership              | primary owner only²     | no | no                   |
 * | delete the workspace                    | primary owner only²     | no | no                   |
 * | edit workspace settings, see all invites| yes   | yes                   | no                   |
 *
 * ¹ Never the primary owner (it must be transferred first) and never the last active owner: there is always one.
 * ² Any owner when the workspace has no primary owner on record.
 * Only active, unrestricted people manage members; agents keep their role (their scope is edited instead), and a
 * deactivated member's role can't change until they're reactivated.
 */
import type { Member, OwnedWorkspace, PendingInvite, Role, Workspace } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { isExpired, isRestricted, organizationName } from "./access.ts";
import { fold } from "./search.ts";

/** Role menu order. */
export const ROLES: Role[] = ["owner", "admin", "member", "guest"];

/** What a decision depends on besides the two people. */
export type RoleContext = {
  /** The workspace's primary owner (absent: none on record). */
  primaryOwnerId?: string;
  /** Active human owners. */
  ownerCount: number;
  now: number;
};

/** Why an action isn't allowed. */
export type RoleProblem =
  /** Not an active, unrestricted owner or admin. */
  | "forbidden"
  /** Owners (or their roles) are managed by owners only; admins don't manage other admins. */
  | "ownersOnly"
  /** The primary owner can't be demoted or deactivated: transfer primary ownership first. */
  | "primaryOwner"
  /** Only the primary owner may do this. */
  | "primaryOnly"
  /** The workspace must keep at least one owner. */
  | "lastOwner"
  /** Agents keep their role; edit their access instead. */
  | "agent"
  /** Deactivated members' roles can't change (reactivate first). */
  | "deactivated"
  /** Already has that role / already in that state. */
  | "unchanged"
  /** Not on yourself. */
  | "self"
  /** The new primary owner must be an active, unrestricted person. */
  | "ineligible";

/** Active human owners. */
export function activeOwnerCount(members: Member[]): number {
  let n = 0;
  for (const m of members) if (m.role === "owner" && !m.deactivated && m.kind === "human") n += 1;
  return n;
}

export function roleContext(workspace: Workspace, members: Member[], now: number): RoleContext {
  const ctx: RoleContext = { ownerCount: activeOwnerCount(members), now };
  if (workspace.primaryOwnerId !== undefined) ctx.primaryOwnerId = workspace.primaryOwnerId;
  return ctx;
}

/** An active, unrestricted person who is an owner or admin. */
export function isManager(me: Member, now: number): boolean {
  return !isExpired(me, now) && me.kind === "human" && !isRestricted(me) && (me.role === "owner" || me.role === "admin");
}

export function isPrimaryOwner(member: Member, ctx: RoleContext): boolean {
  return ctx.primaryOwnerId !== undefined && ctx.primaryOwnerId === member.userId;
}

/** Can `me` give `target` the role `role`? (undefined = yes) */
export function roleChangeProblem(me: Member, target: Member, role: Role, ctx: RoleContext): RoleProblem | undefined {
  if (!isManager(me, ctx.now)) return "forbidden";
  if (target.kind === "agent") return "agent";
  if (target.deactivated) return "deactivated";
  if (target.role === role) return "unchanged";
  if (me.role !== "owner") {
    if (role === "owner" || target.role === "owner") return "ownersOnly";
    if (target.role === "admin" && target.userId !== me.userId) return "ownersOnly";
  }
  if (isPrimaryOwner(target, ctx)) return "primaryOwner";
  if (target.role === "owner" && ctx.ownerCount <= 1) return "lastOwner";
  return undefined;
}

export function canChangeRole(me: Member, target: Member, role: Role, ctx: RoleContext): boolean {
  return roleChangeProblem(me, target, role, ctx) === undefined;
}

/** The role menu for `target`: its current role plus every role `me` may give it (one entry = read-only). */
export function assignableRoles(me: Member, target: Member, ctx: RoleContext): Role[] {
  return ROLES.filter((r) => r === target.role || roleChangeProblem(me, target, r, ctx) === undefined);
}

/**
 * What else a role change does: a restricted member (guest) becoming member/admin/owner loses the restriction (the
 * UI confirms first); someone becoming a guest needs a scope (the UI opens the scope editor).
 */
export function roleChangeEffect(target: Member, role: Role): "clearsRestriction" | "needsScope" | "none" {
  if (role === target.role) return "none";
  if (role === "guest") return isRestricted(target) ? "none" : "needsScope";
  return isRestricted(target) && target.kind === "human" ? "clearsRestriction" : "none";
}

function manageProblem(me: Member, target: Member, ctx: RoleContext): RoleProblem | undefined {
  if (!isManager(me, ctx.now)) return "forbidden";
  if (target.userId === me.userId) return "self";
  if (me.role !== "owner" && (target.role === "owner" || target.role === "admin")) return "ownersOnly";
  return undefined;
}

/** Can `me` deactivate `target`? (undefined = yes) */
export function deactivateProblem(me: Member, target: Member, ctx: RoleContext): RoleProblem | undefined {
  const p = manageProblem(me, target, ctx);
  if (p !== undefined) return p;
  if (target.deactivated) return "unchanged";
  if (isPrimaryOwner(target, ctx)) return "primaryOwner";
  if (target.role === "owner" && ctx.ownerCount <= 1) return "lastOwner";
  return undefined;
}

export function canDeactivate(me: Member, target: Member, ctx: RoleContext): boolean {
  return deactivateProblem(me, target, ctx) === undefined;
}

/** Can `me` reactivate `target`? (undefined = yes) */
export function reactivateProblem(me: Member, target: Member, ctx: RoleContext): RoleProblem | undefined {
  const p = manageProblem(me, target, ctx);
  if (p !== undefined) return p;
  return target.deactivated ? undefined : "unchanged";
}

export function canReactivate(me: Member, target: Member, ctx: RoleContext): boolean {
  return reactivateProblem(me, target, ctx) === undefined;
}

/** Can `me` hand primary ownership to `target`? (undefined = yes) */
export function transferProblem(me: Member, target: Member, ctx: RoleContext): RoleProblem | undefined {
  if (!isManager(me, ctx.now) || me.role !== "owner") return "forbidden";
  if (ctx.primaryOwnerId !== undefined && ctx.primaryOwnerId !== me.userId) return "primaryOnly";
  if (target.userId === me.userId) return "self";
  if (target.deactivated || target.kind !== "human" || isRestricted(target) || isExpired(target, ctx.now)) return "ineligible";
  return undefined;
}

export function canTransferOwnership(me: Member, target: Member, ctx: RoleContext): boolean {
  return transferProblem(me, target, ctx) === undefined;
}

/** Name, URL, icon and default language. */
export function canEditWorkspace(me: Member, now: number): boolean {
  return isManager(me, now);
}

/** Deleting the workspace: the primary owner (any owner when there is none on record). */
export function canDeleteWorkspace(me: Member, ctx: RoleContext): boolean {
  if (!isManager(me, ctx.now) || me.role !== "owner") return false;
  return ctx.primaryOwnerId === undefined || ctx.primaryOwnerId === me.userId;
}

/** Every pending invite (others see only their own). */
export function canSeeAllInvites(me: Member, now: number): boolean {
  return isManager(me, now);
}

/** Revoke or resend: admins and owners, or whoever created the invite. */
export function canManageInvite(me: Member, invite: PendingInvite, now: number): boolean {
  if (isExpired(me, now) || isRestricted(me) || me.kind !== "human") return false;
  return isManager(me, now) || invite.createdBy === me.userId;
}

/** Workspaces whose ownership must be transferred before the account can be deleted (primary or last owner). */
export function deletionBlockers(owned: OwnedWorkspace[]): string[] {
  return owned.filter((w) => w.primary || w.otherOwners === 0).map((w) => w.id);
}

// ---------- Labels ----------

export function roleLabel(role: Role): MessageDescriptor {
  switch (role) {
    case "owner":
      return msg("roles.owner");
    case "admin":
      return msg("roles.admin");
    case "member":
      return msg("roles.member");
    case "guest":
      return msg("roles.guest");
  }
}

/** "Primary owner" / "Owner" / "Admin" next to names on profiles; nothing for members and guests. */
export function roleBadge(member: Member, primaryOwnerId: string | undefined): MessageDescriptor | undefined {
  if (member.deactivated) return undefined;
  if (member.role === "owner") return member.userId === primaryOwnerId ? msg("roles.primaryOwner") : msg("roles.owner");
  if (member.role === "admin") return msg("roles.admin");
  return undefined;
}

export function problemMessage(problem: RoleProblem): MessageDescriptor {
  switch (problem) {
    case "forbidden":
      return msg("roles.problem.forbidden");
    case "ownersOnly":
      return msg("roles.problem.ownersOnly");
    case "primaryOwner":
      return msg("roles.problem.primaryOwner");
    case "primaryOnly":
      return msg("roles.problem.primaryOnly");
    case "lastOwner":
      return msg("roles.problem.lastOwner");
    case "agent":
      return msg("roles.problem.agent");
    case "deactivated":
      return msg("roles.problem.deactivated");
    case "unchanged":
      return msg("roles.problem.unchanged");
    case "self":
      return msg("roles.problem.self");
    case "ineligible":
      return msg("roles.problem.ineligible");
  }
}

const PROBLEM_CODES: [RoleProblem, string][] = [
  ["forbidden", "forbidden"],
  ["ownersOnly", "owners_only"],
  ["primaryOwner", "primary_owner"],
  ["primaryOnly", "primary_only"],
  ["lastOwner", "last_owner"],
  ["agent", "agent_role"],
  ["deactivated", "member_deactivated"],
  ["unchanged", "unchanged"],
  ["self", "self"],
  ["ineligible", "ineligible"],
];

/** The API error code for a refusal (`ApiError.code`). */
export function problemCode(problem: RoleProblem): string {
  for (const [p, code] of PROBLEM_CODES) if (p === problem) return code;
  return "forbidden";
}

/** Back from an API error code (undefined for other errors). */
export function problemFromCode(code: string): RoleProblem | undefined {
  for (const [p, c] of PROBLEM_CODES) if (c === code) return p;
  return undefined;
}

/** "{name} is now an admin." after a role change. */
export function roleChangedNotice(name: string, role: Role): MessageDescriptor {
  switch (role) {
    case "owner":
      return msg("members.notice.nowOwner", { name });
    case "admin":
      return msg("members.notice.nowAdmin", { name });
    case "member":
      return msg("members.notice.nowMember", { name });
    case "guest":
      return msg("members.notice.nowGuest", { name });
  }
}

// ---------- The members table ----------

export type DirectoryFilter = "all" | "active" | "deactivated" | "invited" | "admins" | "guests" | "agents";
export const DIRECTORY_FILTERS: DirectoryFilter[] = ["all", "active", "invited", "deactivated", "admins", "guests", "agents"];

export type DirectoryStatus = "active" | "deactivated" | "invited";

/** One row of the members table: a member, or a pending email invite. */
export type DirectoryRow = {
  /** userId, or "invite:<id>". */
  key: string;
  userId?: string;
  inviteId?: string;
  name: string;
  fullName?: string;
  title?: string;
  /** Only when the viewer may see it. */
  email?: string;
  avatarUrl?: string;
  role: Role;
  kind: "human" | "agent";
  status: DirectoryStatus;
  /** External organization (guests from another company). */
  organization?: string;
  restricted: boolean;
  primary: boolean;
  me: boolean;
  /** The role menu (current role included; one entry = read-only). */
  roles: Role[];
  canDeactivate: boolean;
  canReactivate: boolean;
  /** Edit the access policy (guests and agents: admins or the sponsor). */
  canEditScope: boolean;
  /** Invites: revoke / resend. */
  canManageInvite: boolean;
  canTransfer: boolean;
  /** Restricted members: the accountable person and when access ends. */
  sponsorName?: string;
  expiresAt?: number;
};

function statusOf(m: Member, now: number): DirectoryStatus {
  return m.deactivated || isExpired(m, now) ? "deactivated" : "active";
}

const STATUS_ORDER: Record<DirectoryStatus, number> = { active: 0, invited: 1, deactivated: 2 };

function matchesFilter(row: DirectoryRow, filter: DirectoryFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "active":
    case "deactivated":
    case "invited":
      return row.status === filter;
    case "admins":
      return row.status !== "deactivated" && (row.role === "owner" || row.role === "admin");
    case "guests":
      return row.kind === "human" && (row.restricted || row.role === "guest");
    case "agents":
      return row.kind === "agent";
  }
}

function matchesQuery(row: DirectoryRow, q: string): boolean {
  if (q === "") return true;
  for (const text of [row.name, row.fullName, row.email, row.title, row.organization]) {
    if (text !== undefined && fold(text).includes(q)) return true;
  }
  return false;
}

/**
 * The members table: every member `me` can see plus pending email invites, filtered (`query` matches names, email,
 * title and organization, accent-insensitive) and sorted (active, invited, deactivated; then by name). Each row carries
 * what `me` may do to it (logic above).
 */
export function directoryRows(
  members: Member[],
  invites: PendingInvite[],
  me: Member,
  ctx: RoleContext,
  query: string,
  filter: DirectoryFilter,
): DirectoryRow[] {
  const byId: Record<string, Member> = {};
  for (const m of members) byId[m.userId] = m;
  const rows: DirectoryRow[] = [];
  for (const m of members) {
    const restricted = isRestricted(m);
    const sponsorId = m.access === undefined ? undefined : m.access.sponsorId;
    const row: DirectoryRow = {
      key: m.userId,
      userId: m.userId,
      name: m.displayName,
      role: m.role,
      kind: m.kind,
      status: statusOf(m, ctx.now),
      restricted,
      primary: isPrimaryOwner(m, ctx),
      me: m.userId === me.userId,
      roles: assignableRoles(me, m, ctx),
      canDeactivate: canDeactivate(me, m, ctx),
      canReactivate: canReactivate(me, m, ctx),
      canEditScope: (restricted || m.kind === "agent") && !m.deactivated && !isExpired(me, ctx.now) && !isRestricted(me) && (isManager(me, ctx.now) || sponsorId === me.userId),
      canManageInvite: false,
      canTransfer: canTransferOwnership(me, m, ctx),
    };
    if (m.fullName !== "" && m.fullName !== m.displayName) row.fullName = m.fullName;
    if (m.title !== undefined && m.title !== "") row.title = m.title;
    if (m.email !== undefined) row.email = m.email;
    if (m.avatarUrl !== undefined) row.avatarUrl = m.avatarUrl;
    const org = organizationName(m);
    if (org !== undefined) row.organization = org;
    if (sponsorId !== undefined) {
      const sponsor = byId[sponsorId];
      row.sponsorName = sponsor === undefined ? sponsorId : sponsor.displayName;
    }
    if (m.access !== undefined && m.access.expiresAt !== undefined) row.expiresAt = m.access.expiresAt;
    rows.push(row);
  }
  for (const inv of invites) {
    if (inv.email === undefined) continue;
    rows.push({
      key: `invite:${inv.id}`,
      inviteId: inv.id,
      name: inv.email,
      email: inv.email,
      role: inv.role,
      kind: "human",
      status: "invited",
      restricted: inv.role === "guest",
      primary: false,
      me: false,
      roles: [inv.role],
      canDeactivate: false,
      canReactivate: false,
      canEditScope: false,
      canManageInvite: canManageInvite(me, inv, ctx.now),
      canTransfer: false,
      expiresAt: inv.expiresAt,
    });
  }
  const q = fold(query).trim();
  return rows
    .filter((r) => matchesFilter(r, filter) && matchesQuery(r, q))
    .toSorted((a, b) => {
      const s = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      if (s !== 0) return s;
      const x = fold(a.name);
      const y = fold(b.name);
      return x < y ? -1 : x > y ? 1 : a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
    });
}

/** Counts per filter (the filter pills show them). */
export function directoryCounts(rows: DirectoryRow[]): Record<DirectoryFilter, number> {
  const out: Record<DirectoryFilter, number> = { all: 0, active: 0, deactivated: 0, invited: 0, admins: 0, guests: 0, agents: 0 };
  for (const r of rows) for (const f of DIRECTORY_FILTERS) if (matchesFilter(r, f)) out[f] += 1;
  return out;
}
